import { AgCollapsableChildrenHeaderComponent } from 'components/AgGrid/AgCollapsableChildrenHeaderComponent';
import { DeviatedValueCellRendererComponent } from 'components/AgGrid/DeviatedValueCellRendererComponent';
import { suppressKeyboardEventForInput } from 'components/AgGrid/utils';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import type {
    BimContractConstructionTypeData,
    BimContractResultsViewData,
    BimNormalContractResultData,
} from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { getNumberFormatterWithLinePlaceholder } from 'pages/Project/Common/utils';
import { getLabelWorkOrder } from 'utils/getLabelWorkOrder';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import { ContractPlanCellRendererComponent } from '../components/ContractPlanCellRendererComponent';
import { WorkOrderTitleCellHeaderComponent } from '../components/WorkOrderTitleCellHeaderComponent';

interface IUseAccumulationSheetColumns {
    contractTitle: string;
    onChange?: (id: string, value: number | null) => void;
    isReadOnly?: boolean;
    isContractResults: boolean;
    frameContractResults?: BimContractResultsViewData | null;
    normalContractResults?: (BimNormalContractResultData | null)[] | undefined | null;
    isProjectStatusDelayed?: boolean;
    constructionTypes?: BimContractConstructionTypeData[];
    hasNoPlanByConstructionType?: boolean | null;
    contractCalculationStatus?: WorkOrderStatusEnum;
}

const instanceId = 'bimAccumulationSheetTable';

export const useAccumulationSheetColumns = (props: IUseAccumulationSheetColumns) => {
    const {
        contractTitle,
        onChange,
        isProjectStatusDelayed,
        isReadOnly,
        isContractResults,
        frameContractResults,
        normalContractResults,
        constructionTypes,
        hasNoPlanByConstructionType,
        contractCalculationStatus,
    } = props;

    const sortedConstructionTypes = useMemo(
        () =>
            hasNoPlanByConstructionType
                ? []
                : [...(constructionTypes || [])].sort(
                      (a, b) => (a.name || (a as any).label)?.localeCompare(b.name || (b as any).label),
                  ),
        [constructionTypes, hasNoPlanByConstructionType],
    );

    const [selection, , , setSelection] = useSelection(instanceId);
    const [pinnedItems, , , setPinned] = usePin(instanceId);

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const frameContractResultsColumns = useMemo<ColDef[]>(() => {
        if (!isContractResults || !frameContractResults) {
            return [];
        }

        const workOrderConstructionTypesIds =
            frameContractResults?.contractResults?.[0]?.workOrderResults?.[0]?.constructionTypeResults.map(
                (r) => r.constructionTypeId,
            ) || [];

        const workOrderColumns =
            frameContractResults?.workOrders?.map((workOrder) => ({
                headerGroupComponent: hasNoPlanByConstructionType
                    ? WorkOrderTitleCellHeaderComponent
                    : AgCollapsableChildrenHeaderComponent,
                headerGroupComponentParams: hasNoPlanByConstructionType
                    ? { workOrder }
                    : {
                          displayNameNode: <WorkOrderTitleCellHeaderComponent workOrder={workOrder} />,
                          collapseKeys: sortedConstructionTypes
                              .filter((t) => workOrderConstructionTypesIds.includes(t.id))
                              .reduce(
                                  (acc, constType) => [
                                      ...acc,
                                      ...[
                                          `planPriceWorkOrder${workOrder.id}_${constType.id}`,
                                          `completedPriceWorkOrder${workOrder.id}_${constType.id}`,
                                      ],
                                  ],
                                  [] as string[],
                              ),
                      },
                headerName: getLabelWorkOrder(workOrder as any),
                flex: 1,
                marryChildren: true,
                children: [
                    {
                        headerName: 'Итого',
                        minWidth: 328,
                        children: [
                            {
                                headerName: 'План',
                                field: `planPriceWorkOrder${workOrder.id}`,
                                valueFormatter: getNumberFormatterWithLinePlaceholder(0),
                                type: 'rightAligned',
                                headerClass: 'center-aligned-header',
                                cellRenderer: DeviatedValueCellRendererComponent,
                                cellRendererParams: {
                                    checkDeviation: true,
                                    deviationKey: `deviationWorkOrder${workOrder.id}`,
                                },
                            },
                            {
                                headerName: 'Сумма Актов КС-2',
                                field: `completedPriceWorkOrder${workOrder.id}`,
                                valueFormatter: getNumberFormatterWithLinePlaceholder(0),
                                type: 'rightAligned',
                                headerClass: 'center-aligned-header',
                                minWidth: 200,
                                flex: 1,
                            },
                        ],
                    },
                    ...sortedConstructionTypes.map((constType) => ({
                        headerName: constType.name || (constType as any).label,
                        field: `planAndSumPrice${workOrder.id}_${constType.id}`,
                        minWidth: 328,
                        marryChildren: true,
                        children: [
                            {
                                headerName: 'План',
                                field: `planPriceWorkOrder${workOrder.id}_${constType.id}`,
                                valueFormatter: getNumberFormatterWithLinePlaceholder(0),
                                type: 'rightAligned',
                                headerClass: 'center-aligned-header',
                                initialHide: true,
                            },
                            {
                                headerName: 'Сумма Актов КС-2',
                                field: `completedPriceWorkOrder${workOrder.id}_${constType.id}`,
                                valueFormatter: getNumberFormatterWithLinePlaceholder(0),
                                type: 'rightAligned',
                                headerClass: 'center-aligned-header',
                                initialHide: true,
                            },
                        ],
                    })),
                ],
            })) || [];

        return [
            {
                headerName: 'Сумма планов НЗ',
                field: 'workOrderPlanSum',
                valueFormatter: getNumberFormatterWithLinePlaceholder(0),
                minWidth: 200,
                flex: 1,
                spanHeaderHeight: true,
                type: 'rightAligned',
                headerClass: 'center-aligned-header',
                cellRenderer: DeviatedValueCellRendererComponent,
                cellRendererParams: {
                    checkDeviation: true,
                    deviationKey: 'deviation',
                },
            },
            {
                headerName: 'Остаток с учетом планов НЗ',
                field: 'contractRemainder',
                valueFormatter: getNumberFormatterWithLinePlaceholder(0),
                minWidth: 200,
                flex: 1,
                spanHeaderHeight: true,
                type: 'rightAligned',
                headerClass: 'center-aligned-header',
                cellRenderer: DeviatedValueCellRendererComponent,
                cellRendererParams: {
                    checkDeviationTip: true,
                    deviationTipKey: 'deviationTip',
                },
            },
            {
                headerName: 'Факт освоения по НЗ',
                field: 'completed',
                valueFormatter: getNumberFormatterWithLinePlaceholder(0),
                minWidth: 200,
                flex: 1,
                spanHeaderHeight: true,
                type: 'rightAligned',
                headerClass: 'center-aligned-header',
                cellRenderer: DeviatedValueCellRendererComponent,
                cellRendererParams: {
                    checkDeviation: true,
                    deviationKey: 'completedDeviation',
                },
            },
            {
                headerName: 'Остаток с учетом освоения',
                field: 'completedRemainder',
                valueFormatter: getNumberFormatterWithLinePlaceholder(0),
                minWidth: 200,
                flex: 1,
                spanHeaderHeight: true,
                type: 'rightAligned',
                headerClass: 'center-aligned-header',
                cellRenderer: DeviatedValueCellRendererComponent,
                cellRendererParams: {
                    checkDeviationTip: true,
                    deviationTipKey: 'completedRemainderDeviationTip',
                },
            },
            ...workOrderColumns,
        ];
    }, [frameContractResults, hasNoPlanByConstructionType, isContractResults, sortedConstructionTypes]);

    const normalContractResultsColumns = useMemo<ColDef[]>(() => {
        if (!isContractResults || !normalContractResults || contractCalculationStatus === WorkOrderStatusEnum.Delayed) {
            return [];
        }

        return [
            {
                headerName: 'Остаток по договору',
                field: 'contractRemainder',
                valueFormatter: getNumberFormatterWithLinePlaceholder(0),
                minWidth: 200,
                flex: 1,
                type: 'rightAligned',
                cellRenderer: DeviatedValueCellRendererComponent,
                cellRendererParams: {
                    checkDeviationTip: true,
                    deviationTipKey: 'deviationTip',
                },
            },
            {
                headerName: 'Факт освоения по НЗ',
                minWidth: 200,
                marryChildren: true,
                children: [
                    {
                        headerName: 'Итого',
                        field: 'actKC2CompletedSum',
                        valueFormatter: getNumberFormatterWithLinePlaceholder(0),
                        minWidth: 200,
                        flex: 1,
                        type: 'rightAligned',
                        cellRenderer: DeviatedValueCellRendererComponent,
                        cellRendererParams: {
                            checkDeviation: true,
                            deviationKey: 'deviation',
                        },
                    },
                    ...sortedConstructionTypes.map((constType) => ({
                        headerName: constType.name || (constType as any).label,
                        field: `completedResultPrice${constType.id}`,
                        valueFormatter: getNumberFormatterWithLinePlaceholder(0),
                        minWidth: 200,
                        type: 'rightAligned',
                        headerClass: 'center-aligned-header',
                        flex: 1,
                        cellRenderer: DeviatedValueCellRendererComponent,
                        cellRendererParams: {
                            checkDeviation: true,
                            deviationKey: `completedResultDeviation${constType.id}`,
                        },
                    })),
                ],
            },
        ];
    }, [contractCalculationStatus, isContractResults, normalContractResults, sortedConstructionTypes]);

    const allColumns = useMemo<ColDef[]>(
        () => [
            {
                headerName: contractTitle,
                field: 'name',
                minWidth: 600,
                flex: 1,
                suppressKeyboardEvent: suppressKeyboardEventForInput,
                spanHeaderHeight: true,
            },
            {
                headerName: 'План по договору',
                field: 'contractPlanHeader',
                suppressKeyboardEvent: suppressKeyboardEventForInput,
                marryChildren: true,
                headerGroupComponent:
                    !hasNoPlanByConstructionType &&
                    (frameContractResults ||
                        normalContractResults ||
                        contractCalculationStatus === WorkOrderStatusEnum.Delayed)
                        ? AgCollapsableChildrenHeaderComponent
                        : undefined,
                headerGroupComponentParams:
                    !hasNoPlanByConstructionType &&
                    (frameContractResults ||
                        normalContractResults ||
                        contractCalculationStatus === WorkOrderStatusEnum.Delayed)
                        ? {
                              collapseKeys: sortedConstructionTypes.map((constType) => `contractPlan_${constType.id}`),
                          }
                        : undefined,
                children: [
                    {
                        headerName: 'Итого',
                        field: 'contractPlan',
                        valueFormatter: getNumberFormatterWithLinePlaceholder(0),
                        suppressKeyboardEvent: suppressKeyboardEventForInput,
                        type: 'rightAligned',
                    },
                    ...sortedConstructionTypes.map((constType) => ({
                        headerName: constType.name || (constType as any).label,
                        field: `contractPlan_${constType.id}`,
                        valueFormatter: getNumberFormatterWithLinePlaceholder(0),
                        cellRenderer: ContractPlanCellRendererComponent,
                        cellRendererParams: {
                            isReadOnly,
                            onChange,
                            isProjectStatusDelayed,
                            constructionTypeId: constType.id,
                        },
                        suppressKeyboardEvent: suppressKeyboardEventForInput,
                        initialHide: !!(
                            frameContractResults ||
                            normalContractResults ||
                            contractCalculationStatus === WorkOrderStatusEnum.Delayed
                        ),
                        type: 'rightAligned',
                        headerClass: 'center-aligned-header',
                    })),
                ],
            },
            ...frameContractResultsColumns,
            ...normalContractResultsColumns,
        ],
        [
            contractCalculationStatus,
            contractTitle,
            frameContractResults,
            frameContractResultsColumns,
            hasNoPlanByConstructionType,
            isProjectStatusDelayed,
            isReadOnly,
            normalContractResults,
            normalContractResultsColumns,
            onChange,
            sortedConstructionTypes,
        ],
    );

    const columns = useMemo(() => {
        const columnsWithPins = getColumnsWithPinnedProp({ columns: allColumns as any, pinnedItems });

        return columnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [allColumns, isColumnVisible, pinnedItems]);

    const optionalColumns = useMemo(
        () =>
            allColumns.reduce((acc: ColDef[], column, idx) => {
                const newColumn: any = { ...column };

                if (!idx) {
                    newColumn.disabled = true;
                }

                if (pinnedItems[getColumnId(column as any)]) {
                    newColumn.pinned = true;
                }

                acc.push(newColumn);

                return acc;
            }, []),
        [allColumns, pinnedItems],
    );

    const handleTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: optionalColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [optionalColumns, setSelection, setPinned],
    );

    return {
        columns,
        isColumnVisible,
        handleTableSettingsConfirm,
        optionalColumns,
    };
};
